/**
 * Para ONDE vai o arquivo que a pessoa anexou no chat.
 *
 * O Eka (Marketing Hub) já tinha clipe e mandava tudo para o acervo de
 * MARKETING. O Agente JanusOS não tinha nenhum. Copiar o do Eka mandaria
 * contrato assinado para o catálogo de criativos: o arquivo ficaria
 * catalogado como material de campanha, apareceria na biblioteca de
 * conteúdo e seria elegível para publicar como anúncio.
 *
 * A regra do dono (2026-08-18): *"se o contexto da conversa for marketing
 * hub sobe para o acervo de marketing, se o contexto for algum cliente
 * específico você precisa criar um acervo para cada deal"*.
 *
 * Este módulo implementa isso com um terceiro ramo que a regra não
 * cobria e que acontece o tempo todo — **o widget do Agente JanusOS é
 * GLOBAL**, flutua em toda tela. Abrir o agente no Dashboard, sem deal
 * nenhum em contexto, e anexar um contrato não cai em nenhum dos dois
 * ramos. E os dois contextos também não são exclusivos: dá para estar em
 * `/mkt` com o agente comercial aberto.
 *
 * | contexto | destino |
 * |---|---|
 * | deal em contexto | acervo do deal |
 * | Marketing Hub | acervo de marketing |
 * | **nenhum, ou os dois** | **PERGUNTA** — nunca adivinha |
 *
 * Defaultar em silêncio para marketing seria exatamente o erro que a
 * regra existe para impedir. Defaultar para "nenhum destino" perderia o
 * arquivo. Então a terceira resposta é uma pergunta, não um palpite.
 *
 * Função pura, fora do componente, porque é a decisão que decide onde o
 * documento do cliente vai parar — e ela precisa ser testável sem montar
 * um chat.
 */

export type TipoDeDestino = 'deal' | 'marketing' | 'perguntar';

export interface DestinoDoAnexo {
  tipo: TipoDeDestino;
  /** Só quando `tipo === 'deal'`. */
  dealId?: string;
  /**
   * O que a interface diz ANTES de gravar. O destino aparece sempre: um
   * destino errado tem de ser pego pela pessoa na hora, não descoberto
   * um mês depois quando alguém procura o contrato no lugar errado.
   */
  rotulo: string;
}

/** `/pipeline/<id>` — o id é o segmento seguinte, e só ele. */
const ROTA_DE_DEAL = /^\/pipeline\/([^/?#]+)/;
/** Qualquer coisa sob `/mkt`. */
const ROTA_DE_MARKETING = /^\/mkt(\/|$)/;

/**
 * Segmentos que vêm depois de `/pipeline/` e NÃO são id de deal.
 *
 * Sem esta lista, `/pipeline/novo` viraria "anexar ao deal `novo`" e o
 * upload iria para um id inexistente — 404 do backend com uma mensagem
 * que não explica nada, porque do ponto de vista dele o pedido estava
 * bem formado.
 */
const NAO_SAO_DEAL = new Set(['novo', 'new', 'kanban', 'lista', 'export']);

export function destinoDoAnexo(
  pathname: string,
  opcoes?: {
    /**
     * Um deal que a CONVERSA já nomeou, mesmo fora da tela dele. Vence a
     * rota: se a pessoa está discutindo o deal 412 a partir do dashboard,
     * o contexto real é o 412.
     */
    dealDaConversa?: string | null;
  },
): DestinoDoAnexo {
  const daConversa = (opcoes?.dealDaConversa || '').trim();
  if (daConversa) {
    return {
      tipo: 'deal',
      dealId: daConversa,
      rotulo: 'Vou anexar ao negócio em discussão nesta conversa.',
    };
  }

  const caminho = (pathname || '').split('?')[0].split('#')[0];

  const naRotaDeDeal = ROTA_DE_DEAL.exec(caminho);
  const dealId =
    naRotaDeDeal && !NAO_SAO_DEAL.has(naRotaDeDeal[1].toLowerCase())
      ? naRotaDeDeal[1]
      : null;
  const emMarketing = ROTA_DE_MARKETING.test(caminho);

  // Os DOIS ao mesmo tempo não deveria acontecer pelas rotas atuais, mas
  // se acontecer a resposta é a mesma da ausência: perguntar. Escolher um
  // dos dois num empate é adivinhar com cara de regra.
  if (dealId && !emMarketing) {
    return {
      tipo: 'deal',
      dealId,
      rotulo: 'Vou anexar aos documentos deste negócio.',
    };
  }
  if (emMarketing && !dealId) {
    return {
      tipo: 'marketing',
      rotulo: 'Vou subir para o acervo de marketing.',
    };
  }

  return {
    tipo: 'perguntar',
    rotulo: 'Onde devo guardar este arquivo?',
  };
}

/**
 * O arquivo parece peça de anúncio?
 *
 * Só AVISA, nunca decide. O contexto da pessoa é declaração de intenção
 * mais forte que a nossa inferência a partir de pixels — mas um
 * descasamento silencioso é o caro, então a dúvida aparece.
 *
 * `null` = nada a dizer.
 */
export function avisoDeDescasamento(
  destino: DestinoDoAnexo,
  arquivo: { name: string; type?: string },
): string | null {
  const ehImagem =
    (arquivo.type || '').startsWith('image/') ||
    /\.(png|jpe?g|gif|webp)$/i.test(arquivo.name || '');
  if (destino.tipo === 'deal' && ehImagem) {
    return 'Isto parece uma peça de criativo. Quer mandar para o acervo de marketing em vez do negócio?';
  }
  const ehDocumento = /\.(pdf|docx?|xlsx?|pptx?)$/i.test(arquivo.name || '');
  if (destino.tipo === 'marketing' && ehDocumento) {
    return 'Isto parece um documento comercial. Quer anexar a um negócio em vez do acervo de marketing?';
  }
  return null;
}
